import { Binding } from '@adalo/constants'
import _ from 'lodash'
import {
  CellData,
  Field,
  BindingDataItem,
  ImageValue,
  Dimensions,
  CIRCLE,
  SQUARE,
  PORTRAIT,
  LANDSCAPE,
  CellStyle,
} from './TableTypes'

export interface TableState {
  columns: { label: string; style: CellStyle }[]
  data: {
    id: number
    cells: CellData[]
  }[]
}

export const formatLocation = (field: CellData): string => {
  if (!field.value) {
    return ''
  }

  const bindingFormat = field.binding?.format?.locationFormat ?? ''

  if (
    typeof bindingFormat !== 'string' ||
    !bindingFormat.length ||
    bindingFormat.length === 0
  ) {
    return ''
  }

  const { value } = field
  const keyPath = bindingFormat?.split('.') ?? ''

  return _.get(value, keyPath, '')
}

export const getBindingData = (
  fields: Field[],
  bindingData: BindingDataItem[]
): TableState => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return { columns: [], data: [] }
  }

  const columns = fields.map(({ label, binding }) => ({
    label,
    style: getColumnStyle(binding),
  }))

  const data = bindingData.map(bindingDataItem => {
    const cells = fields?.map?.(field => ({
      fieldId: field.fieldId,
      type: field.type,
      value: bindingDataItem[field.fieldId],
      label: field.label,
      binding: field.binding,
      style: getColumnStyle(field.binding),
    }))

    const { id } = bindingDataItem
    return { id, cells }
  })

  return { columns, data }
}

export const getImageSize = (
  imageValue: ImageValue,
  ratio: string
): Dimensions => {
  switch (ratio) {
    case CIRCLE:
      return { width: 34, height: 34 }
    case SQUARE:
      return { width: 34, height: 34 }
    case PORTRAIT:
      return { width: 34, height: 51 }
    case LANDSCAPE:
      return { width: 51, height: 34 }
    default:
      return { width: imageValue.width, height: imageValue.height }
  }
}

type FormatColumnWidth = 'standard' | 'wide'

const getFormatColumnWidth = (columnWidth?: string): FormatColumnWidth =>
  columnWidth === 'wide' ? 'wide' : 'standard'

const ColumnMinWidthOptions: { [key in FormatColumnWidth]: number } = {
  standard: 90,
  wide: 180,
}

const ColumnFlexOptions: { [key in FormatColumnWidth]: number } = {
  standard: 1,
  wide: 2,
}

export const getColumnStyle = (fieldBinding?: Partial<Binding>) => {
  const columnFormat = getFormatColumnWidth(fieldBinding?.format?.columnWidth)

  return {
    minWidth: ColumnMinWidthOptions[columnFormat],
    flex: ColumnFlexOptions[columnFormat],
  }
}
